// Shared docs content — consumed by both /docs page and <HelpBubble> drawer.
// Keyed by `feature id` for O(1) lookup from anywhere in the app.

export const DOCS = {
  // Getting started
  onboard: {
    section: "Getting Started",
    title: "First-run onboarding",
    why: "Nothing works until your Workspace has S1 + Resend credentials and at least one FB ad account is connected.",
    how: [
      "Admin: open Team & Keys → paste System1 API key + Resend API key.",
      "Each buyer: open My Facebook → connect your personal FB token (never shared across the team).",
      "Verify your vertical map: Dashboard → filter by vertical to confirm campaigns categorize correctly.",
    ],
  },

  // Dashboard
  kpis: {
    section: "Performance Dashboard",
    title: "KPI bar",
    why: "Your whole P&L in under 2 seconds. Profit and ROI are the only two numbers that matter — they always live at the top.",
    how: ["Toggle date range: Today / Yesterday / 7d / 30d.", "Filter by vertical or by individual buyer (admin only) to isolate a team member's performance."],
  },
  table: {
    section: "Performance Dashboard",
    title: "Campaigns table",
    why: "One row per FB campaign joined against System1 revenue. Sort by profit, ROI, or CTR to triage in seconds.",
    how: ["Click the status badge to pause / unpause directly (no trip to Ads Manager).", "Use the sync button if you pushed changes in Meta and want fresh numbers immediately."],
  },

  // Campaign Builder
  brief: {
    section: "Campaign Builder",
    title: "AI Brief (Claude 4.5)",
    why: "Writes 4 hook variants, 3 ad-copy options, 3 CTAs, 3 descriptions, and 6 angle ideas in one pass. Saves the 45-min 'first draft' portion of every launch.",
    how: ["Enter title · vertical · audience · 1-line description.", "Click Generate brief.", "Edit inline if needed, then save — the brief is reusable across images, video, and matrix launch."],
  },
  images: {
    section: "Campaign Builder",
    title: "Image generation (Gemini Nano Banana)",
    why: "Produces 3-6 ad-ready creatives from the brief. Biased toward your winning DNA if Creative DNA is trained.",
    how: ["Click Generate images.", "Regenerate any single image with a tweak prompt.", "Best images auto-flow into Push-to-FB."],
  },
  video: {
    section: "Campaign Builder",
    title: "Informative video slideshow",
    why: "60% of RSOC verticals show higher CTR on video than static. We render a 15-second MP4 from your images + hooks, no editor needed.",
    how: ["Click Generate video. Wait ~30 seconds. Preview → download → attach to ad."],
  },
  matrix: {
    section: "Campaign Builder",
    title: "Matrix Launch (24 variants)",
    why: "One brief × 4 angles × 3 audiences × 2 styles = 24 micro-tests for $1.25/variant. Meta's algorithm learns 10× faster than with single ads.",
    how: ["After generating a brief, click Matrix launch.", "Confirm budget.", "FB pushes all 24 paused — activate whichever subset you want."],
  },

  // Insights
  insights_summary: {
    section: "AI Insights",
    title: "Daily insights summary",
    why: "You shouldn't need to squint at a spreadsheet to know what happened. The summary tells you in 3 sentences.",
    how: ["Open /insights. Read the top card. Action items are in the Winners / Losers / Heatmap below."],
  },

  // Rules
  rule_basics: {
    section: "Auto-Rules",
    title: "Auto-Rules",
    why: "Humans sleep. The market doesn't. Rules keep the good ads running and kill the bad ones while you're offline.",
    how: ["New rule.", "Pick trigger (ROI < X%, spend > $Y, CPA > $Z).", "Pick action (pause / raise / lower budget).", "Click Simulate to preview affected campaigns BEFORE activating.", "Save + enable."],
  },

  // Optimize — 6 tactics
  auto_op: {
    section: "Optimize",
    title: "Auto-Operator",
    why: "One switch turns on fatigue rotation + winner scaling every 30 min, debounced 24h. Wake up to an already-optimized account.",
    how: ["Configure → enable + set thresholds → Save.", "Banner turns blue ARMED.", "'Run now' for instant trigger."],
  },
  matrix_opt: {
    section: "Optimize",
    title: "Matrix Launch",
    why: "4×3×2 = 24 ad variants, $1.25 each. Meta's algo learns 10× faster than single-ad tests.",
    how: ["Pick a saved brief → Preview plan → Launch."],
  },
  dayparting: {
    section: "Optimize",
    title: "Day-parting optimizer",
    why: "You're burning ~35% of night-time spend on dead hours. This finds them and schedules ads ON only in +40% ROI windows.",
    how: ["Review the 24-hour ROI curve → click Apply hot-window schedule.", "Ad sets are scheduled to run only during green hours."],
  },
  fatigue: {
    section: "Optimize",
    title: "Creative fatigue rotator",
    why: "CTR decays ~40% after day 7 on most verticals. Rotating to an A/B twin resets the audience signal and keeps CTR high.",
    how: ["Tab shows ads whose CTR has decayed ≥35% vs peak.", "Click Rotate A/B to swap, or arm Auto-Operator to do this nightly."],
  },
  winners: {
    section: "Optimize",
    title: "Winner-Scaling Protocol",
    why: "Proven formula: +50% ROI at $100+/day for 48h → duplicate 3× with different age brackets at 2× budget. Double every 48h until ROI drops below 20%. Compounds aggressively.",
    how: ["Tab lists qualifying winners.", "Click Scale this winner → 3 duplicates queue.", "History tab shows every scale event."],
  },
  exclusions: {
    section: "Optimize",
    title: "Exclusion Audiences",
    why: "You pay per click — every single one. Meta keeps re-showing ads to people who clicked once and bounced. This excludes them. Expect 15-30% cheaper CPA within a week.",
    how: ["Review the estimated monthly waste at the top.", "Pick ad account + FB Page → Build clickers-minus-converters bundle.", "Select bundle → paste adset IDs → Attach exclusion."],
  },
  circuit: {
    section: "Optimize",
    title: "Budget Circuit Breaker",
    why: "Safety net. If spend exceeds your limit in a short window AND ROI is under your floor, the whole account is paused. Protects against runaway test-launches or broken tracking.",
    how: ["Admin sets spend limit + hours + ROI floor → Enable.", "Runs every 30 min with 1-hour debounce.", "Manual Pause-all button available."],
  },

  // Intelligence — 5 tactics
  dna: {
    section: "Intelligence",
    title: "Creative DNA tagging",
    why: "Extracts a 6-axis DNA (palette, subject, emotion, composition, text, setting) from winning briefs. Future image generation inherits those traits — you're compounding what works instead of rolling the dice on every new creative.",
    how: ["Extract DNA on each past brief to train the model.", "Test → paste any prompt → Enhance with winning DNA to bias output."],
  },
  keywords: {
    section: "Intelligence",
    title: "RSOC Keyword ordering",
    why: "System1 rewards keyword ordering. High-RPC terms should go first. We rank candidates by RPC × √searches so you don't burn tests on low-volume vanity terms.",
    how: ["Select a vertical → review the top-20 ranked keywords → copy the top 5 into your S1 forceKeys param."],
  },

  // Scouting
  scout: {
    section: "Scouting",
    title: "Trend Scouting",
    why: "Being first to a trend is worth 3-5× the RPC vs arriving after saturation. This finds them for you.",
    how: ["Open /scouting. Bookmark any topic. Create a brief from it in one click."],
  },

  // Digest
  digest_arm: {
    section: "Daily Digest",
    title: "Daily Digest setup",
    why: "Your morning coffee becomes your stand-up. 30 seconds of reading replaces 15 minutes of dashboard squinting.",
    how: ["Enable + pick delivery hour + timezone.", "Save → Send test now to verify Resend key.", "Wake up tomorrow to the email."],
  },
  digest_action: {
    section: "Daily Digest",
    title: "One-click Scale / Pause buttons",
    why: "Read digest on the subway. Tap 'Scale 3×' or 'Pause now'. Account is optimized by the time you reach the office. Links are HMAC-signed, single-use, 48h expiry.",
    how: ["Colored buttons appear next to each winner/loser/attention item inside the email. Tap → confirmation page → done."],
  },

  // CAPI
  capi: {
    section: "FB CAPI",
    title: "FB CAPI Postback Bridge",
    why: "The missing link. RSOC runs on pages you don't control, so Meta normally never sees conversions. This bridge catches S1 postbacks and forwards them as Purchase events. Typical 20-40% CPA drop once wired.",
    how: ["Copy your personal postback URL from Team & Keys.", "Paste into System1's 'server postback URL' field.", "Verify fbclid is in your landing URL params."],
  },

  // Team
  invite: {
    section: "Team",
    title: "Invite buyers / viewers",
    why: "Scale beyond you without sharing your personal FB token.",
    how: ["Team page → Invite → email + role → send.", "Accepted user sets their own password."],
  },
  keys: {
    section: "Team",
    title: "Workspace keys",
    why: "Central config. One S1 key + one Resend key serves the entire team.",
    how: ["Admin pastes keys → Save. Team inherits automatically."],
  },

  // Connections
  connect: {
    section: "My Facebook",
    title: "Connect Facebook",
    why: "Each buyer's token is stored only on their account. No key-sharing, no accidental cross-account actions.",
    how: ["Paste access token → Save. Ad accounts load automatically."],
  },
};

export function getDoc(id) {
  return DOCS[id];
}
